import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  fromEvent,
  of,
  Subscription,
  firstValueFrom,
} from 'rxjs';
import {
  catchError,
  map,
  tap,
  throttleTime,
  finalize,
  shareReplay,
} from 'rxjs/operators';
import { initializeApp, getApps, getApp, FirebaseOptions } from 'firebase/app';
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  Messaging,
} from 'firebase/messaging';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import {
  NotificationItem,
  PaginatedNotifications,
  UnreadCountResponse,
} from '../models/notification.model';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private platformId = inject(PLATFORM_ID);

  private readonly baseUrl = `${environment.host}/api/v1/notifications`;
  private messagingInstance: Messaging | null = null;
  private unreadCountRequest$: Observable<number> | null = null;

  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  private notificationsSubject = new BehaviorSubject<NotificationItem[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();

  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  public isLoading$ = this.isLoadingSubject.asObservable();

  private totalDocsSubject = new BehaviorSubject<number>(0);
  public totalDocs$ = this.totalDocsSubject.asObservable();

  private focusSubscription: Subscription | null = null;
  private authSubscription: Subscription | null = null;

  constructor() {
    this.initAuthSync();
    this.initServiceWorker();
  }

  private initServiceWorker(): void {
    if (!isPlatformBrowser(this.platformId) || !('serviceWorker' in navigator))
      return;

    const { vapidKey, measurementId, ...fbConfig } =
      (environment.firebase as Record<string, string>) || {};
    const query = new URLSearchParams(fbConfig).toString();

    navigator.serviceWorker
      .register(query ? `/firebase-messaging-sw.js?${query}` : '/firebase-messaging-sw.js')
      .catch((err) => {
        console.warn(
          '[NotificationService] Service Worker registration failed:',
          err,
        );
      });
  }

  private initAuthSync(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.authSubscription = this.authService.isAuthenticated$.subscribe(
      (isAuthenticated) => {
        if (isAuthenticated) {
          this.fetchUnreadCount().subscribe();
          this.setupFocusListener();
          this.syncExistingPushSubscription();
        } else {
          this.unreadCountSubject.next(0);
          this.notificationsSubject.next([]);
          this.teardownFocusListener();
        }
      },
    );
  }

  private setupFocusListener(): void {
    if (this.focusSubscription || !isPlatformBrowser(this.platformId)) return;

    this.focusSubscription = fromEvent(window, 'focus')
      .pipe(throttleTime(30000, undefined, { leading: true, trailing: false }))
      .subscribe(() => {
        this.fetchUnreadCount().subscribe();
      });
  }

  private teardownFocusListener(): void {
    if (this.focusSubscription) {
      this.focusSubscription.unsubscribe();
      this.focusSubscription = null;
    }
  }

  /**
   * Fetches latest unread count from the API and updates state.
   */
  fetchUnreadCount(): Observable<number> {
    if (this.unreadCountRequest$) {
      return this.unreadCountRequest$;
    }

    this.unreadCountRequest$ = this.http
      .get<UnreadCountResponse>(`${this.baseUrl}/unread-count`)
      .pipe(
        map((res) => res?.unreadCount || 0),
        tap((count) => this.unreadCountSubject.next(count)),
        catchError(() => of(this.unreadCountSubject.value)),
        finalize(() => {
          this.unreadCountRequest$ = null;
        }),
        shareReplay(1),
      );

    return this.unreadCountRequest$;
  }

  /**
   * Fetches paginated notifications feed.
   */
  fetchNotifications(
    page = 1,
    limit = 15,
    unreadOnly = false,
  ): Observable<PaginatedNotifications> {
    this.isLoadingSubject.next(true);

    const params: Record<string, string> = {
      page: String(page),
      limit: String(limit),
      ...(unreadOnly ? { unreadOnly: 'true' } : {}),
    };

    return this.http.get<PaginatedNotifications>(this.baseUrl, { params }).pipe(
      tap((res) => {
        this.isLoadingSubject.next(false);
        const docs = res?.docs?.length ? res.docs : [];
        this.notificationsSubject.next(docs);
        this.totalDocsSubject.next(res?.totalDocs || 0);
      }),
      catchError((err) => {
        this.isLoadingSubject.next(false);
        throw err;
      }),
    );
  }

  /**
   * Marks an individual notification as read.
   */
  markAsRead(notificationId: string): Observable<NotificationItem> {
    return this.http
      .patch<NotificationItem>(`${this.baseUrl}/${notificationId}/read`, {})
      .pipe(
        tap((updated) => {
          const currentList = this.notificationsSubject.value;
          const updatedList = currentList.map((item) =>
            item._id === notificationId ? { ...item, isRead: true } : item,
          );
          this.notificationsSubject.next(updatedList);

          const currentCount = this.unreadCountSubject.value;
          if (currentCount > 0) {
            this.unreadCountSubject.next(currentCount - 1);
          }
        }),
      );
  }

  /**
   * Marks all notifications as read for current user.
   */
  markAllAsRead(): Observable<{ success: boolean; modifiedCount: number }> {
    return this.http
      .patch<{
        success: boolean;
        modifiedCount: number;
      }>(`${this.baseUrl}/mark-all-read`, {})
      .pipe(
        tap(() => {
          const currentList = this.notificationsSubject.value;
          const updatedList = currentList.map((item) => ({
            ...item,
            isRead: true,
          }));
          this.notificationsSubject.next(updatedList);
          this.unreadCountSubject.next(0);
        }),
      );
  }

  /**
   * Deletes a notification from user feed.
   */
  deleteNotification(notificationId: string): Observable<boolean> {
    return this.http
      .delete<{ success: boolean }>(`${this.baseUrl}/${notificationId}`)
      .pipe(
        map((res) => res.success),
        tap((success) => {
          if (success) {
            const currentList = this.notificationsSubject.value;
            const target = currentList.find((i) => i._id === notificationId);
            const filtered = currentList.filter(
              (item) => item._id !== notificationId,
            );
            this.notificationsSubject.next(filtered);

            if (target && !target.isRead) {
              const currentCount = this.unreadCountSubject.value;
              if (currentCount > 0) {
                this.unreadCountSubject.next(currentCount - 1);
              }
            }
          }
        }),
      );
  }

  /**
   * Registers an FCM device push token with the backend.
   */
  registerDeviceToken(
    token: string,
    platform: 'web' | 'android' | 'ios' = 'web',
  ): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${this.baseUrl}/devices`, {
      token,
      platform,
    });
  }

  /**
   * Unregisters an FCM device token.
   */
  unregisterDeviceToken(token: string): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${this.baseUrl}/devices/${encodeURIComponent(token)}`,
    );
  }

  /**
   * Prepends a newly received real-time notification into the feed.
   */
  prependNotification(item: NotificationItem): void {
    const currentList = this.notificationsSubject.value;
    // Prevent duplicate entries
    if (currentList.some((i) => i._id === item._id)) return;

    this.notificationsSubject.next([item, ...currentList]);
    if (!item.isRead) {
      this.unreadCountSubject.next(this.unreadCountSubject.value + 1);
    }
  }

  private async getFirebaseMessaging(): Promise<Messaging | null> {
    if (!isPlatformBrowser(this.platformId)) return null;
    const supported = await isSupported().catch(() => false);
    if (!supported) return null;

    if (!this.messagingInstance) {
      const fbConfig = environment.firebase as
        | (FirebaseOptions & { vapidKey?: string })
        | undefined;
      if (!fbConfig?.apiKey || !fbConfig?.projectId) {
        console.warn(
          '[NotificationService] Incomplete environment.firebase configuration',
        );
        return null;
      }

      const app = getApps().length ? getApp() : initializeApp(fbConfig);
      this.messagingInstance = getMessaging(app);

      onMessage(this.messagingInstance, () => {
        this.fetchUnreadCount().subscribe();
      });
    }

    return this.messagingInstance;
  }

  /**
   * Prompts the user for push notification permission and subscribes via FCM.
   * On success, registers the push token with the backend.
   */
  async requestPushSubscription(): Promise<boolean> {
    if (
      !isPlatformBrowser(this.platformId) ||
      !('Notification' in window) ||
      !('serviceWorker' in navigator)
    ) {
      return false;
    }

    try {
      if ((await Notification.requestPermission()) !== 'granted') return false;
      return await this.syncExistingPushSubscription();
    } catch (err) {
      console.error(
        '[NotificationService] requestPushSubscription failed:',
        err,
      );
      return false;
    }
  }

  private async syncExistingPushSubscription(): Promise<boolean> {
    if (
      !isPlatformBrowser(this.platformId) ||
      !('Notification' in window) ||
      !('serviceWorker' in navigator) ||
      Notification.permission !== 'granted'
    ) {
      return false;
    }

    try {
      const messaging = await this.getFirebaseMessaging();
      if (!messaging) return false;

      const vapidKey = environment.firebase?.vapidKey;
      if (!vapidKey) {
        console.warn(
          '[NotificationService] Missing environment.firebase.vapidKey',
        );
        return false;
      }

      const reg = await navigator.serviceWorker.ready;
      const token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: reg,
      });

      if (token) {
        await firstValueFrom(this.registerDeviceToken(token, 'web'));
        return true;
      }
    } catch (err) {
      console.error(
        '[NotificationService] syncExistingPushSubscription failed:',
        err,
      );
    }

    return false;
  }
}
